import { createSlice } from '@reduxjs/toolkit';
import { permissionsAct } from '../action';
import {
  AgencyUserType,
  PermissionRecordType,
  AgencyContactType,
  EmployeePermissionRecordType,
} from '@nxsys/types/models/permissions';
import {
  NotificationMessageType,
  PaginationParamsTypeWithSearch,
  PaginationType,
} from '@nxsys/types/models/Common';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';
import { CandidateType } from '@nxsys/types/models/permissions/candidate';
import {
  CompanyUserGroupRecordType,
  CompanyUserRecordType,
  BureauCompanyUserRecordType,
} from '@nxsys/types/models/permissions/company';
import { GetListAgencyUsersParams, GetListCandidatesParams, GetListCompanyUserGroupsParams, GetListCompanyUsersParams, GetListEmployeesPermissionsParams } from '../types';

interface PermissionsState {
  agencyPermissions: PermissionRecordType[];
  subcontractorPermissions: PermissionRecordType[];
  candidatePermissions: PermissionRecordType[];
  companyPermissions: PermissionRecordType[];
  agencyUsers: {
    list: AgencyUserType[];
    pagination: PaginationType;
    queryParams: GetListAgencyUsersParams | null;
  };
  agencyContacts: {
    list: AgencyContactType[];
    pagination: PaginationType;
  };
  candidatesData: {
    list: CandidateType[];
    pagination: PaginationType;
    queryParams: GetListCandidatesParams | null;
  };
  companyUsersData: {
    list: CompanyUserRecordType[];
    pagination: PaginationType;
    queryParams: GetListCompanyUsersParams | null;
  };
  companyUserGroupsData: {
    list: CompanyUserGroupRecordType[];
    pagination: PaginationType;
    queryParams: GetListCompanyUserGroupsParams | null;
  };
  bureauCompanyUsersData: {
    list: BureauCompanyUserRecordType[];
    pagination: PaginationType;
    queryParams: PaginationParamsTypeWithSearch | null;
  };
  agencyContactsLoading: boolean;
  loading: boolean;
  agencyPermissionsLoading: boolean;
  candidatePermissionsLoading: boolean;
  companyPermissionsLoading: boolean;
  messageNotification: NotificationMessageType | null;
  addUserGroupLoading: boolean;
  employeesPermissionsData: {
    list: EmployeePermissionRecordType[];
    pagination: PaginationType;
    queryParams: GetListEmployeesPermissionsParams | null;
  };
}

export const initialState: PermissionsState = {
  agencyPermissions: [],
  subcontractorPermissions: [],
  candidatePermissions: [],
  companyPermissions: [],
  agencyUsers: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyContacts: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
  },
  candidatesData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  companyUsersData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  companyUserGroupsData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  bureauCompanyUsersData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  loading: false,
  agencyPermissionsLoading: false,
  agencyContactsLoading: false,
  candidatePermissionsLoading: false,
  companyPermissionsLoading: false,
  messageNotification: null,
  addUserGroupLoading: false,
  employeesPermissionsData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
};

const permissionsSlice = createSlice({
  name: 'permissionsSlice',
  initialState: initialState,
  reducers: {
    clearMessage: (state) => {
      state.messageNotification = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      permissionsAct.getListAgencyPermissions.pending,
      (state) => {
        state.agencyPermissionsLoading = true;
      },
    );
    builder.addCase(
      permissionsAct.getListAgencyPermissions.fulfilled,
      (state, action) => {
        state.agencyPermissions = action.payload.data;
        state.agencyPermissionsLoading = false;
      },
    );
    builder.addCase(
      permissionsAct.getListAgencyPermissions.rejected,
      (state) => {
        state.agencyPermissionsLoading = false;
      },
    );
    builder.addCase(permissionsAct.getListAgencyUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.getListAgencyUsers.fulfilled,
      (state, action) => {
        state.agencyUsers = {
          list: action.payload.data,
          pagination: action.payload.pagination,
          queryParams: action.payload.queryParams,
        };
        state.loading = false;
      },
    );
    builder.addCase(permissionsAct.getListAgencyUsers.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(
      permissionsAct.updateAgencyPortallAccess.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyPortallAccess.fulfilled,
      (state, action) => {
        state.loading = false;
        let message = '';
        if (action.payload?.data?.allowPortalAccess) {
          message = 'permissions.portalAccessEnabledSuccess';
        } else {
          message = 'permissions.portalAccessDisabledSuccess';
        }
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyPortallAccess.rejected,
      (state, action) => {
        state.loading = false;
        const message = 'permissions.portalAccessUpdateError';
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyPermissionsAll.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyPermissionsAll.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.permissionsAgencyUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyPermissionsAll.rejected,
      (state, action) => {
        state.loading = false;
        const message = 'permissions.permissionsUpdatedError';
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(permissionsAct.updateAgencyPermissions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.updateAgencyPermissions.fulfilled,
      (state, action) => {
        state.loading = false;
        state.agencyUsers.list = state.agencyUsers.list.map((item) =>
          item.id === action.payload.data.id
            ? { ...item, permissions: action.payload.data.permissions }
            : item,
        );
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.permissionsAgencyUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyPermissions.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.permissionsUpdatedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.getListAgencyContacts.pending, (state) => {
      state.agencyContactsLoading = true;
    });
    builder.addCase(
      permissionsAct.getListAgencyContacts.fulfilled,
      (state, action) => {
        state.agencyContacts = {
          list: action.payload.data,
          pagination: action.payload.pagination,
        };
        state.agencyContactsLoading = false;
      },
    );
    builder.addCase(permissionsAct.getListAgencyContacts.rejected, (state) => {
      state.agencyContactsLoading = false;
    });
    builder.addCase(
      permissionsAct.updateAgencyContactPermissions.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyContactPermissions.fulfilled,
      (state, action) => {
        state.loading = false;
        state.agencyContacts.list = state.agencyContacts.list.map((item) =>
          item.id === action.payload.data.id
            ? { ...item, permissions: action.payload.data.permissions }
            : item,
        );
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.agencyContact.permissionsUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAgencyContactPermissions.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.agencyContact.permissionsUpdatedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.getListCandidates.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.getListCandidates.fulfilled,
      (state, action) => {
        state.loading = false;
        state.candidatesData = {
          list: action.payload.data,
          pagination: action.payload.pagination,
          queryParams: action.payload.queryParams,
        };
      },
    );
    builder.addCase(permissionsAct.getListCandidates.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(
      permissionsAct.updateCandidatePortalAccess.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateCandidatePortalAccess.fulfilled,
      (state, action) => {
        state.loading = false;
        state.candidatesData.list = state.candidatesData.list.map((item) =>
          item.id === action.payload.data.id
            ? {
              ...item,
              allowPortalLogin: action.payload.data.allowPortalLogin,
            }
            : item,
        );
        let message = '';
        if (action.payload?.data?.allowPortalLogin) {
          message = 'permissions.portalAccessEnabledSuccess';
        } else {
          message = 'permissions.portalAccessDisabledSuccess';
        }
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateCandidatePortalAccess.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.portalAccessUpdatedError',
            }),
        });
      },
    );
    builder.addCase(
      permissionsAct.getListCandidatePermissions.pending,
      (state) => {
        state.candidatePermissionsLoading = true;
      },
    );
    builder.addCase(
      permissionsAct.getListCandidatePermissions.fulfilled,
      (state, action) => {
        state.candidatePermissionsLoading = false;
        state.candidatePermissions = action.payload.data;
      },
    );
    builder.addCase(
      permissionsAct.getListCandidatePermissions.rejected,
      (state) => {
        state.candidatePermissionsLoading = false;
      },
    );
    builder.addCase(
      permissionsAct.updateCandidatePermissions.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateCandidatePermissions.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.candidate.permissionsUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateCandidatePermissions.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.permissionsUpdatedError',
            }),
        });
      },
    );
    builder.addCase(
      permissionsAct.setAllCandidatesOfAgencyPermissions.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.setAllCandidatesOfAgencyPermissions.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.permissionsUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.setAllCandidatesOfAgencyPermissions.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.permissionsUpdatedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.getListCompanyUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.getListCompanyUsers.fulfilled,
      (state, action) => {
        state.loading = false;
        state.companyUsersData = {
          list: action.payload.data,
          pagination: action.payload.pagination,
          queryParams: action.payload.queryParams,
        };
      },
    );
    builder.addCase(permissionsAct.getListCompanyUsers.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(
      permissionsAct.getListCompanyUserGroups.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.getListCompanyUserGroups.fulfilled,
      (state, action) => {
        state.loading = false;
        state.companyUserGroupsData = {
          list: action.payload.data,
          pagination: action.payload.pagination,
          queryParams: action.payload.queryParams,
        };
      },
    );
    builder.addCase(
      permissionsAct.getListCompanyUserGroups.rejected,
      (state) => {
        state.loading = false;
      },
    );
    builder.addCase(
      permissionsAct.updateCompanyUserPortalAccess.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateCompanyUserPortalAccess.fulfilled,
      (state, action) => {
        state.loading = false;
        state.companyUsersData.list = state.companyUsersData.list.map((item) =>
          item.id === action.payload.data.id ? action.payload.data : item,
        );
        let message = '';
        if (action.payload?.data?.isBlocked) {
          message = 'permissions.portalAccessDisabledSuccess';
        } else {
          message = 'permissions.portalAccessEnabledSuccess';
        }
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateCompanyUserPortalAccess.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.portalAccessUpdatedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.updateCompanyUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(permissionsAct.updateCompanyUser.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'permissions.user.updatedSuccess',
        }),
      });
    });
    builder.addCase(
      permissionsAct.updateCompanyUser.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.user.updatedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.createCompanyUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(permissionsAct.createCompanyUser.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'permissions.user.createdSuccess',
        }),
      });
    });
    builder.addCase(
      permissionsAct.createCompanyUser.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.user.createdError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.deleteUserGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(permissionsAct.deleteUserGroup.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'permissions.userGroup.deletedSuccess',
        }),
      });
    });
    builder.addCase(
      permissionsAct.deleteUserGroup.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.userGroup.deletedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.createUserGroup.pending, (state) => {
      state.addUserGroupLoading = true;
    });
    builder.addCase(permissionsAct.createUserGroup.fulfilled, (state) => {
      state.addUserGroupLoading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'permissions.userGroup.createdSuccess',
        }),
      });
    });
    builder.addCase(
      permissionsAct.createUserGroup.rejected,
      (state, action) => {
        state.addUserGroupLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.userGroup.createdError',
            }),
        });
      },
    );
    builder.addCase(
      permissionsAct.getListCompanyPermissions.pending,
      (state) => {
        state.companyPermissionsLoading = true;
      },
    );
    builder.addCase(
      permissionsAct.getListCompanyPermissions.fulfilled,
      (state, action) => {
        state.companyPermissionsLoading = false;
        state.companyPermissions = action.payload.data;
      },
    );
    builder.addCase(
      permissionsAct.getListCompanyPermissions.rejected,
      (state) => {
        state.companyPermissionsLoading = false;
      },
    );
    builder.addCase(permissionsAct.updateUserGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(permissionsAct.updateUserGroup.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'permissions.userGroup.updatedSuccess',
        }),
      });
    });
    builder.addCase(
      permissionsAct.updateUserGroup.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.userGroup.updatedError',
            }),
        });
      },
    );
    builder.addCase(
      permissionsAct.getListEmployeesPermissions.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.getListEmployeesPermissions.fulfilled,
      (state, action) => {
        state.loading = false;
        state.employeesPermissionsData = {
          list: action.payload.data,
          pagination: action.payload.pagination,
          queryParams: action.payload.queryParams,
        };
      },
    );
    builder.addCase(
      permissionsAct.getListEmployeesPermissions.rejected,
      (state) => {
        state.loading = false;
      },
    );
    builder.addCase(
      permissionsAct.updateEmployeePermissions.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateEmployeePermissions.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.employee.permissionsUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateEmployeePermissions.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.employee.permissionsUpdatedError',
            }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateEmployeePortalAccess.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateEmployeePortalAccess.fulfilled,
      (state, action) => {
        state.loading = false;
        state.employeesPermissionsData.list =
          state.employeesPermissionsData.list.map((item) =>
            item.id === action.payload.data.id ? action.payload.data : item,
          );
        let message = '';
        if (action.payload?.data?.allowPortalLogin) {
          message = 'permissions.portalAccessEnabledSuccess';
        } else {
          message = 'permissions.portalAccessDisabledSuccess';
        }
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateEmployeePortalAccess.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.employee.portalAccessUpdatedError',
            }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAllEmployeesOfEmployerPermissions.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateAllEmployeesOfEmployerPermissions.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.permissionsUpdatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateAllEmployeesOfEmployerPermissions.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.permissionsUpdatedError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.getBureauCompanyUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.getBureauCompanyUsers.fulfilled,
      (state, action) => {
        state.loading = false;
        state.bureauCompanyUsersData = {
          list: action.payload.data,
          pagination: {
            current: action.payload.pagination.current,
            pageSize: action.payload.pagination.pageSize,
            totalDocs: action.payload.pagination.totalDocs,
          },
          queryParams: action.payload.queryParams,
        };
      },
    );
    builder.addCase(permissionsAct.getBureauCompanyUsers.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(
      permissionsAct.updateBureauCompanyUserPortalAccess.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      permissionsAct.updateBureauCompanyUserPortalAccess.fulfilled,
      (state, action) => {
        state.loading = false;
        state.bureauCompanyUsersData.list =
          state.bureauCompanyUsersData.list.map((item) =>
            item.id === action.payload.data.id ? action.payload.data : item,
          );
        let message = '';
        if (action.payload?.data?.isBlocked) {
          message = 'permissions.portalAccessDisabledSuccess';
        } else {
          message = 'permissions.portalAccessEnabledSuccess';
        }
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: message }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateBureauCompanyUserPortalAccess.rejected,
      (state) => {
        state.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'permissions.user.portalAccessUpdatedError',
          }),
        });
      },
    );
    builder.addCase(permissionsAct.createBureauCompanyUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.createBureauCompanyUser.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.user.createdSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.createBureauCompanyUser.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.user.createdError',
            }),
        });
      },
    );
    builder.addCase(permissionsAct.updateBureauCompanyUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      permissionsAct.updateBureauCompanyUser.fulfilled,
      (state) => {
        state.loading = false;
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'permissions.user.updatedSuccess',
          }),
        });
      },
    );
    builder.addCase(
      permissionsAct.updateBureauCompanyUser.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'permissions.user.updatedError',
            }),
        });
      },
    );
  },
});

export const permissionsReducer = permissionsSlice.reducer;
export const permissionAction = {
  ...permissionsSlice.actions,
  ...permissionsAct,
};
